import { useState, useEffect, useRef } from 'react';
import { router } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/cn';
import { Button, Card } from '../../../components/ui';
import ThemeToggle from '../../../components/ui/ThemeToggle';
import { truncateWords } from '../../../lib/truncateWords';
import {
  ArticleDisplay,
  VideoDisplay,
  ImageDisplay,
  UrlDisplay,
  PdfDisplay,
} from '../../../components/stimulus';

export default function PracticeRead({
  student,
  session,
  stimulus,
  activity,
}) {
  const { t } = useTranslation();
  const [showHeaderTitle, setShowHeaderTitle] = useState(false);
  const stimulusRef = useRef(null);

  // Track scroll position to show/hide title in header
  useEffect(() => {
    const handleScroll = () => {
      // Threshold: when user scrolls past ~200px (roughly past the stimulus title)
      const scrollThreshold = 200;
      setShowHeaderTitle(window.scrollY > scrollThreshold);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleContinueToQuestions = () => {
    router.visit(`/student/practice/${session.id}/questions`);
  };

  const handleSaveAndExit = () => {
    router.visit('/student/dashboard');
  };

  const renderStimulus = () => {
    switch (stimulus.stimulus_type) {
      case 'article':
        return (
          <ArticleDisplay
            title={stimulus.title || activity.title}
            subtitle={activity.description}
            content={stimulus.content}
            estimatedReadTime={stimulus.estimated_read_time}
            vocabProps={{
              userRole: "student",
              sourceType: "Stimulus",
              sourceId: stimulus.id,
            }}
          />
        );
      case 'video':
        return (
          <VideoDisplay
            title={stimulus.title || activity.title}
            subtitle={activity.description}
            url={stimulus.url}
            description={activity.description}
          />
        );
      case 'image':
        return (
          <ImageDisplay
            title={stimulus.title || activity.title}
            subtitle={activity.description}
            url={stimulus.url}
            content={stimulus.content}
            description={activity.description}
          />
        );
      case 'url':
        return (
          <UrlDisplay
            title={stimulus.title || activity.title}
            subtitle={activity.description}
            url={stimulus.url}
            description={activity.description}
          />
        );
      case 'pdf':
        return (
          <PdfDisplay
            title={stimulus.title || activity.title}
            subtitle={activity.description}
            fileUrl={stimulus.file_url}
            pageCount={stimulus.page_count}
          />
        );
      default:
        return (
          <ArticleDisplay
            title={stimulus.title || activity.title}
            subtitle={activity.description}
            content={stimulus.content}
            estimatedReadTime={stimulus.estimated_read_time}
            vocabProps={{
              userRole: "student",
              sourceType: "Stimulus",
              sourceId: stimulus.id,
            }}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950">
      {/* Minimal Header with animated title on scroll */}
      <header className="sticky top-0 z-50 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-b border-gray-100 dark:border-gray-700">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <span className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider flex-shrink-0">
                {t('practice.step1of3')}
              </span>
              <span className="text-sm text-gray-600 dark:text-gray-400 flex-shrink-0">
                {t('practice.reading')}
              </span>
              {/* Animated activity title that slides in when scrolled */}
              <div
                className={cn(
                  'overflow-hidden transition-all duration-300 ease-out',
                  showHeaderTitle ? 'max-w-md opacity-100' : 'max-w-0 opacity-0'
                )}
              >
                <span className="text-sm font-medium text-gray-900 dark:text-white block truncate pl-4 border-l border-gray-200 dark:border-gray-600">
                  {truncateWords(stimulus.title || activity.title)}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <ThemeToggle />
              <Button variant="ghost" size="sm" onClick={handleSaveAndExit}>
                {t('practice.saveAndExit')}
              </Button>
            </div>
          </div>
          {/* Progress bar */}
          <div className="mt-3 h-1 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
            <div className="h-full w-1/3 bg-gray-900 dark:bg-white rounded-full transition-all duration-500" />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Stimulus Content */}
        <div ref={stimulusRef} className="mb-8">
          {renderStimulus()}
        </div>

        {/* Offline Encouragement */}
        <Card className="bg-gradient-to-r from-gray-50 to-white dark:from-gray-900 dark:to-gray-950 border border-gray-200 dark:border-gray-700 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-5 h-5 text-gray-600 dark:text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
              </div>
              <div>
                <p className="text-gray-900 dark:text-white font-medium">
                  {t('practice.takeYourTime')}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {t('practice.considerPrinting')}
                </p>
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => window.print()}
              className="flex-shrink-0"
            >
              <svg
                className="w-4 h-4 mr-1.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                />
              </svg>
              {t('common.print')}
            </Button>
          </div>
        </Card>

        {/* Continue Button */}
        <div className="flex justify-center">
          <Button
            variant="primary"
            size="lg"
            onClick={handleContinueToQuestions}
            className="min-w-[240px]"
          >
            {t('practice.finishedReading')}
            <svg
              className="ml-2 w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </Button>
        </div>
      </main>
    </div>
  );
}
